import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Chip,
  Grid,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { API } from '../services/api';
import { showSuccess, showError } from '../utils/notifications';
import DataTable from '../components/DataTable';
import FilterBar from '../components/FilterBar';
import ConfirmDialog from '../components/ConfirmDialog';
import ActionButton from '../components/ActionButton';
import TableSkeleton from '../components/TableSkeleton';

const MotionBox = motion(Box);

function Users() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, userId: null });
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [formData, setFormData] = useState({
    email: '',
    mobile: '',
    companyid: '',
    usertype: 3,
    status: 1,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, companiesRes] = await Promise.all([
        API.users.list(),
        API.companies.list(),
      ]);
      setUsers(usersRes?.data || []);
      setCompanies(companiesRes?.data || []);
    } catch (err) {
      showError('Failed to load users');
      console.error('Users fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email || '',
        mobile: user.mobile || '',
        companyid: user.companyid || '',
        usertype: user.usertype || 3,
        status: user.status !== undefined ? user.status : 1,
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        mobile: '',
        companyid: '',
        usertype: 3,
        status: 1,
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingUser(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        await API.users.update(editingUser.userid, formData);
        showSuccess('User updated successfully!');
      } else {
        await API.users.create(formData);
        showSuccess('User created successfully!');
      }
      handleCloseDialog();
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        (editingUser ? 'Failed to update user' : 'Failed to create user');
      showError(errorMessage);
      console.error('User submit error:', err);
    }
  };

  const handleDeleteClick = (userId) => {
    setConfirmDialog({ open: true, userId });
  };

  const handleDelete = async () => {
    try {
      await API.users.delete(confirmDialog.userId);
      showSuccess('User deleted successfully!');
      fetchData();
      setConfirmDialog({ open: false, userId: null });
    } catch (err) {
      showError('Failed to delete user');
      console.error('User delete error:', err);
    }
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return '-';
    const company = companies.find(c => c.company_id === companyId);
    return company ? company.company_name : 'Unknown';
  };

  const getUserTypeLabel = (type) => {
    switch (type) {
      case 1: return 'Admin';
      case 2: return 'Manager';
      case 3: return 'User';
      default: return 'Unknown';
    }
  };

  const getUserTypeColor = (type) => {
    switch (type) {
      case 1: return { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5' }; // Admin - Red
      case 2: return { bg: '#FEF3C7', color: '#92400E', border: '#FCD34D' }; // Manager - Yellow
      case 3: return { bg: '#DBEAFE', color: '#1E40AF', border: '#93C5FD' }; // User - Blue
      default: return { bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' };
    }
  };

  const getStatusColor = (status) => {
    return status === 1 
      ? { bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7' }
      : { bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' };
  };

  const getStatusLabel = (status) => {
    return status === 1 ? 'Active' : 'Inactive';
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.mobile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getCompanyName(user.companyid)?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || user.status === parseInt(statusFilter);
    const matchesUserType = userTypeFilter === 'all' || user.usertype === parseInt(userTypeFilter);
    const matchesCompany = companyFilter === 'all' || user.companyid === companyFilter;
    
    return matchesSearch && matchesStatus && matchesUserType && matchesCompany;
  });

  const columns = [
    {
      field: 'email',
      headerName: 'Email',
      width: '20%',
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.email}
        </Typography>
      ),
    },
    {
      field: 'mobile',
      headerName: 'Mobile',
      width: '12%',
      render: (row) => row.mobile || '-',
    },
    {
      field: 'companyid',
      headerName: 'Company',
      width: '15%',
      render: (row) => getCompanyName(row.companyid),
    },
    {
      field: 'usertype',
      headerName: 'User Type',
      width: '10%',
      render: (row) => {
        const colors = getUserTypeColor(row.usertype);
        return (
          <Chip
            label={getUserTypeLabel(row.usertype)}
            size="small"
            sx={{ 
              fontWeight: 600,
              fontSize: '0.7rem',
              backgroundColor: colors.bg,
              color: colors.color,
              border: `1px solid ${colors.border}`,
            }}
          />
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: '10%',
      render: (row) => {
        const colors = getStatusColor(row.status);
        return (
          <Chip
            label={getStatusLabel(row.status)}
            size="small"
            sx={{ 
              fontWeight: 600,
              fontSize: '0.7rem',
              backgroundColor: colors.bg,
              color: colors.color,
              border: `1px solid ${colors.border}`,
            }}
          />
        );
      },
    },
    {
      field: 'isemailverified',
      headerName: 'Email Verified',
      width: '10%',
      render: (row) => (
        <Chip
          label={row.isemailverified ? 'Yes' : 'No'}
          size="small"
          sx={{ 
            fontWeight: 600,
            fontSize: '0.7rem',
            backgroundColor: row.isemailverified ? '#D1FAE5' : '#F3F4F6',
            color: row.isemailverified ? '#065F46' : '#6B7280',
            border: `1px solid ${row.isemailverified ? '#6EE7B7' : '#D1D5DB'}`,
          }}
        />
      ),
    },
    {
      field: 'lastloginat',
      headerName: 'Last Login',
      width: '12%',
      render: (row) => row.lastloginat ? new Date(row.lastloginat).toLocaleDateString() : 'Never',
    },
    {
      field: 'createdat',
      headerName: 'Created',
      width: '11%',
      render: (row) => row.createdat ? new Date(row.createdat).toLocaleDateString() : '-',
    },
  ];

  const filterFields = [
    {
      label: 'Status',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: '1', label: 'Active' },
        { value: '0', label: 'Inactive' },
      ],
    },
    {
      label: 'User Type',
      value: userTypeFilter,
      onChange: setUserTypeFilter,
      options: [
        { value: 'all', label: 'All Types' },
        { value: '1', label: 'Admin' },
        { value: '2', label: 'Manager' },
        { value: '3', label: 'User' },
      ],
    },
    {
      label: 'Company',
      value: companyFilter,
      onChange: setCompanyFilter,
      options: [
        { value: 'all', label: 'All Companies' },
        ...companies.map(company => ({ value: company.company_id, label: company.company_name })),
      ],
    },
  ];

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            Users
          </Typography>
        </Box>
        <TableSkeleton columns={8} rows={6} />
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage user accounts and permissions
          </Typography>
        </Box>
        <ActionButton
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add User
        </ActionButton>
      </Box>

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search users..."
        filterFields={filterFields}
      />

      <DataTable
        columns={columns}
        data={filteredUsers}
        onEdit={handleOpenDialog}
        onDelete={(row) => handleDeleteClick(row.userid)}
        getRowId={(row) => row.userid}
        emptyMessage="No users found"
      />

      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
        }}>
          <Typography variant="h6" fontWeight={700}>
            {editingUser ? 'Edit User' : 'Add New User'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={editingUser !== null}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Company"
                value={formData.companyid}
                onChange={(e) => setFormData({ ...formData, companyid: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value="">None</MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.company_id} value={company.company_id}>
                    {company.company_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="User Type"
                value={formData.usertype}
                onChange={(e) => setFormData({ ...formData, usertype: parseInt(e.target.value) })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value={1}>Admin</MenuItem>
                <MenuItem value={2}>Manager</MenuItem>
                <MenuItem value={3}>User</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: parseInt(e.target.value) })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <ActionButton variant="outlined" onClick={handleCloseDialog}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" onClick={handleSubmit}>
            {editingUser ? 'Update User' : 'Create User'}
          </ActionButton>
        </Box>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, userId: null })}
        onConfirm={handleDelete}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        confirmText="Delete"
        type="danger"
      />
    </MotionBox>
  );
}

export default Users;
